import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { CodeAcceptanceStatus } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/react';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { RadioCard, RadioGroup } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { maskEmail, maskMobile } from '../../utils';
import { FormContactFieldsType, VerificationDetailsType } from '../ContactFields';

type UpdateFieldProps = {
  verificationDetails?: VerificationDetailsType;
  onSubmit: (data: FormContactFieldsType) => void;
  isSubmitting: boolean;
} & UseFormReturn<FormContactFieldsType>;

export const UpdateFields = ({ watch, control, handleSubmit, onSubmit, setValue, isSubmitting }: UpdateFieldProps) => {
  const [codeAccepted, userMobile, userEmail] = watch(['codeAccepted', 'mobile', 'email']);
  const isMobileConfirmed = codeAccepted === CodeAcceptanceStatus.Sms;
  const [selected, setSelected] = useState<string>('');

  const field = isMobileConfirmed ? 'email' : 'number';
  const newField = isMobileConfirmed ? 'newEmail' : 'newMobile';

  const fieldValue = isMobileConfirmed ? userEmail : userMobile;

  const handleChange = (value: string) => {
    setValue(newField, value === 'newValue' ? '' : fieldValue);
    setSelected(value);
  };

  return (
    <Stack gap="xlarge">
      <Alert tone="positive">
        <Text weight="bold">{isMobileConfirmed ? 'Mobile number' : 'Email address'} is verified</Text>
      </Alert>
      <Text size="xlarge">Thanks, can you confirm your current {field}?</Text>
      <Text>
        We need to verify that we have the correct {isMobileConfirmed ? 'email address' : 'mobile number'} for your
        account.
      </Text>
      <Stack gap="large">
        <RadioGroup name={isMobileConfirmed ? 'email' : 'mobile'} value={selected} onChange={handleChange}>
          <RadioCard value={isMobileConfirmed ? userEmail : userMobile}>
            <Text>{isMobileConfirmed ? maskEmail(userEmail!) : maskMobile(userMobile)}</Text>
          </RadioCard>
          <RadioCard value="newValue">
            <Text>Enter a new {field}</Text>
          </RadioCard>

          {selected === 'newValue' ? (
            <Box paddingTop="xlarge">
              <TextInputField
                control={control}
                name={newField}
                label={`New ${isMobileConfirmed ? 'email address' : 'mobile number'}`}
              />
            </Box>
          ) : null}
        </RadioGroup>
        {selected ? (
          <Button
            rounded
            css={css({ width: 200 })}
            onClick={() => {
              handleSubmit(onSubmit)();
              datadogRum.addAction(`Reconfirm ${field} - Send code button clicked`);
            }}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Send code
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};
